import React from "react";
import PropTypes from "prop-types";
import Section from "../common/Section";
import DownloadUploadFiles from "../common/DownloadUploadFiles";
import Row from "../common/Row";
import * as types from "../../constants/ComponentConstants";
import * as domainConstants from "../../constants/DomainConstants";
import Form from "../common/Form";
import Fieldset from "../common/Fieldset";
import NumberInput from "../common/NumberInput";
import RadioButtonGroup from "../common/RadioButtonGroup";
import RadioButtonChild from "../common/RadioButtonWithChild";
import InfrastructurenInDeBuurtTable from "../bovenLokaliteitPerInfrastructuur/InfrastructurenInDeBuurtTable";
import Button from "../common/Button";
import { orderByPropertyName } from "../../util/orderBy";
import Map from "../common/map/Map";
import Markers from "../common/map/Markers";
import { Circle } from 'react-leaflet';

const SimulatiePerInfrastructuurForm = (props) => {
	const {
		infrastructuur,
		simulatiePerInfrastructuur,
		disabled,
		onSave,
		onChange,
		fileSessionStarted,
		errors,
		color,
		inEditMode,
		documenten,
		sjablonen,
		fileTypes,
		onDropFiles,
		onDeleteFile,
		center,
		onAddInfrastructuur,
		onRemoveSportInfrastructuur,
		onEdit,
		newCoordinates,
		mapKey,
		hideAndereSoortInfrastructuur,
		topSportSjabloon,
		maxMapFeaturesToLoad,
		osmBaseLayerUrl,
		validationMessageType
	} = props;

	const jaNee = [
		{ value: "true", label: 'Ja' },
		{ value: "false", label: 'Nee' }
	];

	// documentuploadHelpers
	function mapDropFiles(component) {
		return function (files) {
			onDropFiles(component, files);
		};
	}

	function mapDeleteFile(component) {
		return function (fileName) {
			onDeleteFile(component, fileName);
		};
	}

	function getFilesForSjabloonComponent(component) {
		if (sjablonen) {
			const folder = sjablonen.find(x => x.name === component);
			if (folder) return folder.files;
		}
		return [];
	}

	function getFilesForDossierComponent(component) {
		if (documenten) {
			const folder = documenten.find(x => x.name === component);
			if (folder) return folder.files;
		}
		return [];
	}

	const hasCoordinates = simulatiePerInfrastructuur.breedtegraad && simulatiePerInfrastructuur.lengtegraad;

	if (simulatiePerInfrastructuur.sportinfrastructurenInDeBuurt == null) {
		simulatiePerInfrastructuur.sportinfrastructurenInDeBuurt = [];
	}

	if (simulatiePerInfrastructuur.sportinfrastructurenOpKaart == null) {
		simulatiePerInfrastructuur.sportinfrastructurenOpKaart = [];
	}
	const markersToShow = [{
		lat: parseFloat(inEditMode ? newCoordinates.lat : simulatiePerInfrastructuur.breedtegraad),
		long: parseFloat(inEditMode ? newCoordinates.lng : simulatiePerInfrastructuur.lengtegraad),
		size: 'x-large',
		color: types.COLOR_ORANGE,
		tooltip: props.tooltip,
		isStar: true
	}];

	simulatiePerInfrastructuur.sportinfrastructurenOpKaart.filter(i => i.spakkiid && i.tienMinutenWandelen).forEach((sportinfrastructuur, i) => {
		if (sportinfrastructuur.breedtegraad && sportinfrastructuur.lengtegraad)
			markersToShow.push({
				lat: parseFloat(sportinfrastructuur.breedtegraad),
				long: parseFloat(sportinfrastructuur.lengtegraad),
				tooltip: sportinfrastructuur.naam,
				size: 'x-large',
				color: types.COLOR_BLUE
			});
		else
			return "";
	});

	simulatiePerInfrastructuur.sportinfrastructurenOpKaart.filter(i => !i.spakkiid && i.tienMinutenWandelen).forEach((sportinfrastructuur, i) => {
		if (sportinfrastructuur.breedtegraad && sportinfrastructuur.lengtegraad)
			markersToShow.push({
				lat: parseFloat(sportinfrastructuur.breedtegraad),
				long: parseFloat(sportinfrastructuur.lengtegraad),
				tooltip: sportinfrastructuur.naam,
				size: 'x-large',
				color: types.COLOR_TURQUOISE_DARK
			});
		else
			return "";
	});

	simulatiePerInfrastructuur.sportinfrastructurenOpKaart.filter(i => !i.tienMinutenWandelen).forEach((sportinfrastructuur, i) => {
		if (sportinfrastructuur.breedtegraad && sportinfrastructuur.lengtegraad)
			markersToShow.push({
				lat: parseFloat(sportinfrastructuur.breedtegraad),
				long: parseFloat(sportinfrastructuur.lengtegraad),
				tooltip: sportinfrastructuur.naam,
				size: 'x-large',
				color: types.COLOR_YELLOW
			});
		else
			return "";
	});

	const circleCenter = [parseFloat(inEditMode ? newCoordinates.lat : simulatiePerInfrastructuur.breedtegraad), parseFloat(inEditMode ? newCoordinates.lng : simulatiePerInfrastructuur.lengtegraad)];
	const mijnMarkers = [<Markers key="theMarker" markersToShow={markersToShow} />, <Circle key="theCircle" center={circleCenter} color="red" radius={600} fillOpacity={0} />];

	const TOPSPORTGELEGENHEID_TOOLTIP = "Dit betreft sportinfrastructuur waar er topsportwerking aanwezig is of zal zijn i.s.m. een topsportfederatie (in geval van nieuwbouw).";

	const TOPSPORTGELEGENHEID_TOOLTIP_WITH_SJABLOON = "In het definitieve aanvraagformulier moet het ingevulde sjabloon topsport kunnen worden toegevoegd om te kunnen scoren op dit criterium. Zie onderstaande link.";
	const AANTALBEDDEN_TOOLTIP = "Het minimaal aantal beschikbare bedden werd vastgelegd op 40. Indien jouw project dus over minstens 40 bedden in eigen beheer beschikt leidt dit tot een hogere mate van bovenlokaliteit.";

	const topsportprotocol = (<Row>
		<DownloadUploadFiles
			dossierId={simulatiePerInfrastructuur.dossierId}
			title="Sjabloon"
			disabled={disabled}
			color={types.COLOR_PURPLE}
			size={6}
			tab={domainConstants.DocumentTab.BovenLokaliteit}
			component={domainConstants.ComponentName.TopsportProtocol}
			files={getFilesForSjabloonComponent(domainConstants.ComponentName.TopsportProtocol)}
			fileSessionStarted={fileSessionStarted} />

		<DownloadUploadFiles
			dossierId={simulatiePerInfrastructuur.dossierId}
			infrastructuurId={simulatiePerInfrastructuur.infrastructuurId}
			title="Topsport protocol"
			disabled={disabled}
			color={types.COLOR_PURPLE}
			size={6}
			tab={domainConstants.DocumentTab.BovenLokaliteit}
			component={domainConstants.ComponentName.TopsportProtocol}
			files={getFilesForDossierComponent(domainConstants.ComponentName.TopsportProtocol)}
			onDelete={mapDeleteFile(domainConstants.ComponentName.TopsportProtocol)}
			onDrop={mapDropFiles(domainConstants.ComponentName.TopsportProtocol)}
			fileTypes={fileTypes.TopsportProtocol}
			validationMessage={errors ? errors.ulTopsportProtocol : ''}
			validationMessageType={validationMessageType}
			fileSessionStarted={fileSessionStarted} />

	</Row>);

	let sectionTitle = "Duid de juiste ligging aan op de kaart en bekijk de infrastructuren in de buurt";
	let formTitle = "Simulatie ";

	const infrasInProject = orderByPropertyName(simulatiePerInfrastructuur.sportinfrastructurenInDeBuurt.filter(i => i.behoortTotMijnProject), "naam");
	const infrasBinnenCirkel = orderByPropertyName(simulatiePerInfrastructuur.sportinfrastructurenInDeBuurt.filter(i => i.behoortTotMijnProject === false && i.tienMinutenWandelen), "naam");
	const infrasBuitenCirkel = orderByPropertyName(simulatiePerInfrastructuur.sportinfrastructurenInDeBuurt.filter(i => i.behoortTotMijnProject === false && i.tienMinutenWandelen === false), "naam");

	const soortenInfrastructuur = hideAndereSoortInfrastructuur ? props.soortenInfrastructuur.filter((x, i) => i > 0) : props.soortenInfrastructuur;

	const legendeCustomRows = [
		<Row key="legende1"><i className="fa fa-star" style={{ color: "#ff8200", fontSize: "1.5em" }}/>&nbsp;&nbsp;{infrastructuur.display}</Row>,
		<Row key="legende2"><i className="fa fa-map-marker" style={{ color: "#39b4e8", fontSize: "1.5em" }}/>&nbsp;&nbsp;Infrastructuren uit de sportdatabank</Row>,
		<Row key="legende3"><i className="fa fa-map-marker" style={{ color: "#033e51", fontSize: "1.5em" }}/>&nbsp;&nbsp;Manueel toegevoegde infrastructuren</Row>,
		<Row key="legende4"><i className="fa fa-map-marker" style={{ color: "#ffd100", fontSize: "1.5em" }}/>&nbsp;&nbsp;Infrastructuren op meer dan 600m</Row>
	];

	return (
		<Section title={sectionTitle} color={color}>
			<Form
				title={formTitle + ' "' + infrastructuur.display + '"'}
				name="bovenLokaliteit"
				rightButtonLabel="Bewaar"
				onRightButtonClick={onSave}
				rightButtonDisabled={disabled}>
				<Fieldset>
					<Row>
						<NumberInput
							label="Oppervlakte in m²"
							name="oppervlakte"
							onChange={onChange}
							value={simulatiePerInfrastructuur.oppervlakte != null ? simulatiePerInfrastructuur.oppervlakte.toString() : ""}
							size={4}
							disabled={disabled}
							validationMessage={errors ? errors.oppervlakte : ''}
							validationMessageType={validationMessageType}
							tooltip="Oppervlakte van de sportinfrastructuur. Voor openluchtsportvelden is dit de oppervlakte van het sportveld inclusief de uitloopzones en sportgerelateerde randinfrastructuur (dug-outs, kleedkamers, berging, tribune, ...), voor indoor infrastructuur is dit de geprojecteerde vloeroppervlakte van het gebouw of van de constructie. De oppervlakte ingenomen door de parking, wordt niet meegerekend."
							normalWhiteSpace
						/>
						<NumberInput
							label="Hoogte in m"
							name="hoogte"
							onChange={onChange}
							value={simulatiePerInfrastructuur.hoogte != null ? simulatiePerInfrastructuur.hoogte.toString() : ""}
							size={4}
							disabled={disabled}
							validationMessage={errors ? errors.hoogte : ''}
							validationMessageType={validationMessageType}
							tooltip="Hoogte van het gebouw of van de constructie. Ingrepen op de omgeving die nodig zijn om te voldoen aan vergunningsvoorwaarden zoals geluidsbermen bvb, dienen ook opgenomen te worden. Verlichtingspalen en afsluitingen worden niet meegerekend. Ook randinfrastructuur met beperkte hoogte zoals dug-outs, veiligheids- en vangnetten, e.d. worden niet meegerekend."
							normalWhiteSpace
						/>
						<NumberInput
							label="Volume in m³"
							name="volume"
							onChange={onChange}
							value={simulatiePerInfrastructuur.volume != null ? simulatiePerInfrastructuur.volume.toString() : ""}
							size={4}
							disabled={disabled}
							validationMessage={errors ? errors.volume : ''}
							validationMessageType={validationMessageType}
							tooltip="Volume van het gebouw of van de constructie boven het maaiveld. Ondergrondse ruimten worden niet meegerekend! Ingrepen op de omgeving die nodig zijn om te voldoen aan vergunningsvoorwaarden zoals geluidsbermen bvb, dienen ook opgenomen te worden."
							normalWhiteSpace
						/>
					</Row>
					<Row>
						{topSportSjabloon ? <div className="u-clear">
							<RadioButtonGroup
								label={"Topsportgelegenheid"}
								name="topsportGelegenheid"
								value={simulatiePerInfrastructuur.topsportGelegenheid}
								onChange={onChange}
								disabled={disabled}
								items={jaNee}
								horizontal
								validationMessage={errors ? errors.topsportGelegenheid : ''}
								validationMessageType={validationMessageType}
								tooltip={TOPSPORTGELEGENHEID_TOOLTIP_WITH_SJABLOON}
								tooltipWindowWidth={35}
								tooltipWindowAlignRight={false}
								size={6}
							/>
							<div className="o-col-6">
								<a href={topSportSjabloon} className="externe-link minimal classic" target="_blank"><i className="fa fa-external-link"/> Toelichting topsportgebruik</a>
							</div>
						</div> :
							<RadioButtonChild
								label={"Topsportgelegenheid"}
								name="topsportGelegenheid"
								value={simulatiePerInfrastructuur.topsportGelegenheid}
								onChange={onChange}
								disabled={disabled}
								items={jaNee}
								horizontal
								validationMessage={errors ? errors.topsportGelegenheid : ''}
								validationMessageType={validationMessageType}
								tooltip={TOPSPORTGELEGENHEID_TOOLTIP}
								tooltipWindowWidth={35}
								tooltipWindowAlignRight={false}
								childJa={topsportprotocol}
							/>}
						<NumberInput
							label="Aantal bedden"
							name="aantalBedden"
							optioneel
							onChange={onChange}
							value={simulatiePerInfrastructuur.aantalBedden != null ? simulatiePerInfrastructuur.aantalBedden.toString() : ""}
							size={3}
							disabled={disabled}
							noDecimals
							validationMessage={errors ? errors.aantalBedden : ''}
							validationMessageType={validationMessageType}
							tooltip={AANTALBEDDEN_TOOLTIP}
							normalWhiteSpace
						/>
					</Row>
				</Fieldset>
				<div>
					{hasCoordinates ?
						<div>
							<Fieldset title="Sportinfrastructuren in de buurt">
								<Fieldset>
									<Row>
										<Map
											mapName={mapKey}
											maxMapFeaturesToLoad={maxMapFeaturesToLoad}
											baseLayerUrl={osmBaseLayerUrl}
											baseLayerAttribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors, Tiles courtesy of <a href="https://geo6.be/">GEO-6</a>'
											children={mijnMarkers}
											zoom={15}
											legendeCustomRows={legendeCustomRows}
											showEmptyMap
											onlyBaseLayers
											customCenter={center}
										/>
									</Row>
									<Row>
										<p>&nbsp;</p>
										<p>
											De sportinfrastructuren die opgenomen zijn in onze sportdatabank en die zich bevinden in de buurt van deze infrastructuur "{infrastructuur.display}" zijn weergegeven op de kaart en in de onderstaande tabellen.
									<br /><br />
										</p>
										<p>&nbsp;</p>
									</Row>
								</Fieldset>
							</Fieldset>
							{infrasInProject.length > 0 ?
								<Fieldset title="Sportinfrastructuren waarvoor subsidies worden gevraagd">
									<Row>
										<InfrastructurenInDeBuurtTable
											infrastructurenInDeBuurt={infrasInProject}
											disabled
											soortenInfrastructuur={soortenInfrastructuur}
											soortenInfrastructuurAll={props.soortenInfrastructuurAll}
										/>
									</Row>
								</Fieldset>
								:
								null
							}
							<Fieldset title="Sportinfrastructuren in de buurt die zich bevinden binnen een straal van 600m van deze infrastructuur">
								<Row>
									{infrasBinnenCirkel.length > 0 ?
										<InfrastructurenInDeBuurtTable
											infrastructurenInDeBuurt={infrasBinnenCirkel}
											disabled={disabled}
											onDelete={onRemoveSportInfrastructuur}
											onEdit={onEdit}
											soortenInfrastructuur={soortenInfrastructuur}
											soortenInfrastructuurAll={props.soortenInfrastructuurAll}
										/>
										:
										null
									}
								</Row>
							</Fieldset>
							{infrasBuitenCirkel.length > 0 ?
								<Fieldset title="Sportinfrastructuren in de buurt die zich bevinden buiten een straal van 600m van deze infrastructuur">
									<Row>
										<InfrastructurenInDeBuurtTable
											infrastructurenInDeBuurt={infrasBuitenCirkel}
											soortenInfrastructuur={soortenInfrastructuur}
											soortenInfrastructuurAll={props.soortenInfrastructuurAll}
											onDelete={onRemoveSportInfrastructuur}
											onEdit={onEdit}
										/>
									</Row>
								</Fieldset>
								:
								null
							}
							<Fieldset>
								<Row>
									<Button text="Voeg ontbrekende sportinfrastructuur toe" onClick={onAddInfrastructuur} disabled={disabled} />
									<br /><br />
								</Row>
								<Row>
									<p>
										Volgende sportinfrastructuren kunnen eventueel toegevoegd worden aan de lijst:
									<br /><br />

										<ul>
											<li>
												Bestaande sportinfrastructuur die niet opgenomen is in de sportdatabank.
										</li>
											<li>
												Nog niet gerealiseerde sportinfrastructuur die in een vorige subsidieronde werd gesubsidieerd.
										</li>
											<li>
												Nog niet gerealiseerde sportinfrastructuur waarvoor reeds een vergunning werd bekomen.
										</li>
										</ul>
										<br />
										Let op: de toegevoegde sportinfrastructuren moeten zich binnen een straal van 600m bevinden.
								</p>
								</Row>
							</Fieldset>
						</div>
						: ""}
				</div>
			</Form>
		</Section >
	);
};

SimulatiePerInfrastructuurForm.propTypes = {
	infrastructuur: PropTypes.object.isRequired,
	simulatiePerInfrastructuur: PropTypes.object.isRequired,
	soortenInfrastructuur: PropTypes.array.isRequired,
	soortenInfrastructuurAll: PropTypes.array.isRequired,
	disabled: PropTypes.bool,
	onSave: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired,
	onDropFiles: PropTypes.func,
	onDeleteFile: PropTypes.func,
	fileSessionStarted: PropTypes.bool,
	errors: PropTypes.object,
	color: PropTypes.string.isRequired,
	inEditMode: PropTypes.bool,
	documenten: PropTypes.array,
	sjablonen: PropTypes.array,
	fileTypes: PropTypes.object,
	center: PropTypes.object.isRequired,
	hideAndereSoortInfrastructuur: PropTypes.bool.isRequired,
	onAddInfrastructuur: PropTypes.func,
	onChangeAdres: PropTypes.func,
	onRemoveSportInfrastructuur: PropTypes.func,
	onEdit: PropTypes.func,
	newCoordinates: PropTypes.object,
	mapKey: PropTypes.string,
	topSportSjabloon: PropTypes.string.isRequired,
	maxMapFeaturesToLoad: PropTypes.string,
	osmBaseLayerUrl: PropTypes.string,
	tooltip: PropTypes.string,
	validationMessageType: PropTypes.string
};

export default SimulatiePerInfrastructuurForm;
